/**
 * <animated-richtext>
 *
 * Single, section-agnostic custom element that drives every whole-block
 * reveal mode for the text-fx system (split-chars, split-words, blur,
 * shiny, typing). Highlight styling itself (solid-color, gradient,
 * underline, handwritten) is pure CSS driven off the --in-view class this
 * element toggles, see assets/component-text-effects.css.
 *
 * Progressive enhancement contract: if this script never runs, the element
 * keeps its server-rendered text content untouched and fully visible —
 * no class here ever hides real text outside of a state this file itself
 * creates first.
 */
(function () {
  if (customElements.get('animated-richtext')) return;

  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class AnimatedRichtext extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.animation || 'none';
      this.scrollTriggered = this.dataset.scroll !== 'false';
      this.threshold = parseFloat(this.dataset.threshold) || 0.15;
      this.rootMargin = this.dataset.rootMargin || '0px 0px -10% 0px';
      this.speed = parseInt(this.dataset.speed, 10) || 500;
      this.stagger = parseInt(this.dataset.delay, 10) || 40;

      const reducedMotion = window.matchMedia && window.matchMedia(REDUCED_MOTION_QUERY).matches;
      if (reducedMotion) {
        // Final state, no animation: underline/handwritten/shiny render in
        // their static CSS state already; split/blur/typing are skipped
        // entirely so the original text is never touched.
        this.classList.add('text-fx__inner--in-view');
        return;
      }

      if (this.mode === 'typing') {
        this.setupTyping();
        return;
      }

      if (this.mode === 'split-chars' || this.mode === 'split-words' || this.mode === 'blur') {
        const unit = this.mode === 'split-chars' ? 'char' : 'word';
        const run = () => {
          this.splitTextNodes(unit);
          this.classList.add('text-fx__inner--armed');
          this.triggerReveal(() => this.classList.add('text-fx__inner--in-view'));
        };
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(run);
        } else {
          run();
        }
        return;
      }

      // none / shiny / highlight-only: no DOM mutation needed up front.
      this.classList.add('text-fx__inner--armed');
      this.triggerReveal(() => this.classList.add('text-fx__inner--in-view'));
    }

    /** Fires `callback` on scroll-into-view, or on next frame if immediate. */
    triggerReveal(callback) {
      if (!this.scrollTriggered) {
        requestAnimationFrame(callback);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              callback();
              observer.unobserve(this);
            }
          });
        },
        { threshold: this.threshold, rootMargin: this.rootMargin }
      );
      observer.observe(this);
    }

    /**
     * Wraps every non-whitespace character or word of this element's text
     * nodes in a span carrying --text-fx-index, preserving surrounding
     * markup (e.g. the highlighted <strong> spans) untouched.
     */
    splitTextNodes(unit) {
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, null, false);
      const textNodes = [];
      let node;
      while ((node = walker.nextNode())) {
        if (node.textContent.trim().length > 0) textNodes.push(node);
      }

      const className = unit === 'char' ? 'text-fx__char' : 'text-fx__word';
      let index = 0;

      textNodes.forEach((textNode) => {
        const text = textNode.textContent;
        const leading = text.match(/^\s*/)[0];
        const trailing = text.match(/\s*$/)[0];
        const trimmed = text.trim();
        if (!trimmed) return;

        const fragment = document.createDocumentFragment();
        if (leading) fragment.appendChild(document.createTextNode(leading));

        const pieces = unit === 'char' ? trimmed.split('') : trimmed.split(/\s+/);
        pieces.forEach((piece, i) => {
          if (unit === 'char' && piece === ' ') {
            fragment.appendChild(document.createTextNode(' '));
            return;
          }
          const span = document.createElement('span');
          span.className = className;
          span.style.setProperty('--text-fx-index', index);
          span.textContent = piece;
          fragment.appendChild(span);
          index += 1;
          if (unit === 'word' && i < pieces.length - 1) {
            fragment.appendChild(document.createTextNode(' '));
          }
        });

        if (trailing) fragment.appendChild(document.createTextNode(trailing));
        textNode.parentNode.replaceChild(fragment, textNode);
      });
    }

    /** Typewriter effect with optional blinking cursor and delete/loop. */
    setupTyping() {
      const originalText = this.textContent.trim();
      const loop = this.dataset.typingLoop === 'true';
      const showCursor = this.dataset.typingCursor !== 'false';
      const typingSpeed = this.speed;
      const initialDelay = this.stagger;
      const pauseDuration = parseInt(this.dataset.typingPause, 10) || 1200;
      const deletingSpeed = parseInt(this.dataset.typingDeleteSpeed, 10) || 30;

      this.textContent = '';
      const contentSpan = document.createElement('span');
      contentSpan.className = 'text-fx__typing-content';
      this.appendChild(contentSpan);

      if (showCursor) {
        const cursorSpan = document.createElement('span');
        cursorSpan.className = 'text-fx__cursor';
        cursorSpan.setAttribute('aria-hidden', 'true');
        cursorSpan.textContent = '|';
        this.appendChild(cursorSpan);
      }

      let charIndex = 0;
      let deleting = false;

      const step = () => {
        if (deleting) {
          if (charIndex <= 0) {
            deleting = false;
            if (!loop) return;
            setTimeout(step, pauseDuration);
            return;
          }
          charIndex -= 1;
          contentSpan.textContent = originalText.slice(0, charIndex);
          setTimeout(step, deletingSpeed);
          return;
        }

        if (charIndex < originalText.length) {
          charIndex += 1;
          contentSpan.textContent = originalText.slice(0, charIndex);
          setTimeout(step, typingSpeed);
          return;
        }

        if (loop) {
          setTimeout(() => {
            deleting = true;
            step();
          }, pauseDuration);
        }
      };

      this.classList.add('text-fx__inner--armed');
      this.triggerReveal(() => setTimeout(step, initialDelay));
    }
  }

  customElements.define('animated-richtext', AnimatedRichtext);
})();
